import React from "react";

import QueryStatsTable, {
  QueryStatsTableSortBy,
} from "features/Queries/QueryStatsTable";

import { useQueryStringSort } from "utils/hooks";

import {
  ServerGroupPerDatabaseQueryTableQuery,
  useServerGroupPerDatabaseQueryTableQuery,
} from "./gql/Query.generated";

interface Props {
  serverGroupId: string;
  datname: string;
  startTs: number;
  endTs: number;
  statementTypes: Array<string>;
  searchTerm: null | string;
  compare: boolean;
}

const FETCH_BATCH_SIZE = 100;

function QueryTable({
  serverGroupId,
  datname,
  startTs,
  endTs,
  statementTypes,
  searchTerm,
  compare,
}: Props) {
  const [sortOpts, setSortOpts] = useQueryStringSort<QueryStatsTableSortBy>({
    param: "sort",
    sortBy: "pctOfTotal",
    sortDirection: "DESC",
  });

  const { data, loading, error, fetchMore } =
    useServerGroupPerDatabaseQueryTableQuery({
      variables: {
        serverGroupId,
        datname,
        startTs,
        endTs,
        statementTypes,
        offset: 0,
        limit: FETCH_BATCH_SIZE,
        filter: searchTerm,
        sortBy: sortOpts.sortBy,
        sortDirection: sortOpts.sortDirection,
        compare,
      },
    });

  const fetchMoreData = (loadedCount: number) => {
    return new Promise<boolean>((resolve) => {
      const offset = loadedCount;
      fetchMore({
        variables: { offset, limit: FETCH_BATCH_SIZE },
        updateQuery: (
          prev,
          { fetchMoreResult },
        ): ServerGroupPerDatabaseQueryTableQuery => {
          const fetched = fetchMoreResult.getQueryStatsByDatabaseGroup;
          resolve(fetched.length > 0);
          if (!prev || !prev.getQueryStatsByDatabaseGroup) {
            return fetchMoreResult;
          }
          return {
            ...prev,
            getQueryStatsByDatabaseGroup: [
              ...prev.getQueryStatsByDatabaseGroup,
              ...fetched,
            ],
          };
        },
      });
    });
  };

  return (
    <QueryStatsTable
      queries={data?.getQueryStatsByDatabaseGroup ?? []}
      showServer
      loading={loading && !data}
      error={!!error}
      noRowsText={
        searchTerm
          ? "No queries match your search."
          : "No query statistics available."
      }
      fetchMoreData={fetchMoreData}
      sortOpts={sortOpts}
      setSortOpts={setSortOpts}
      searchTerm={searchTerm}
      compare={compare}
    />
  );
}

export default QueryTable;
